import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { FaPlus, FaTrash, FaEdit, FaSearch, FaUndo } from 'react-icons/fa';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'proteinas', label: '🍖 Proteínas' },
  { value: 'carbohidratos', label: '🍞 Carbohidratos' },
  { value: 'carbohidratos_simples', label: '🍯 Carbos Simples' },
  { value: 'grasas', label: '🥑 Grasas' },
  { value: 'vegetales', label: '🥬 Vegetales' },
  { value: 'frutas', label: '🍎 Frutas' }
];

export default function FoodManager() {
  const [foods, setFoods] = useState({});
  const [customFoods, setCustomFoods] = useState([]);
  const [excludedFoods, setExcludedFoods] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('proteinas');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newFood, setNewFood] = useState({
    name: '',
    category: 'proteinas',
    subcategory: '',
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
    unit: '100g',
    portion_g: 100
  });

  useEffect(() => {
    loadFoods();
    loadCustomFoods();
    loadExcludedFoods();
  }, []);

  const loadFoods = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/offline-generator/food-database`);
      const data = await res.json();
      setFoods(data);
    } catch (error) {
      console.error('Error loading foods:', error);
      toast.error('Error al cargar alimentos');
    }
  };

  const loadCustomFoods = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/offline-generator/custom-foods`);
      const data = await res.json();
      setCustomFoods(data);
    } catch (error) {
      console.error('Error loading custom foods:', error);
    }
  };

  const loadExcludedFoods = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/offline-generator/excluded-foods`);
      const data = await res.json();
      setExcludedFoods(data);
    } catch (error) {
      console.error('Error loading excluded foods:', error);
    }
  };

  const handleAddFood = async () => {
    if (!newFood.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/offline-generator/custom-foods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFood)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Error al agregar');
      }

      toast.success(`Alimento "${newFood.name}" agregado`);
      setIsAddDialogOpen(false);
      setNewFood({
        name: '',
        category: 'proteinas',
        subcategory: '',
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        fiber: 0,
        unit: '100g',
        portion_g: 100
      });
      loadFoods();
      loadCustomFoods();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Abrir dialog de edición
  const handleOpenEdit = (food) => {
    setEditingFood({
      ...food,
      category: selectedCategory,
      originalName: food.name,
      isCustom: food.is_custom || false
    });
    setIsEditDialogOpen(true);
  };

  // Guardar edición
  const handleSaveEdit = async () => {
    if (!editingFood) return;

    try {
      if (editingFood.isCustom) {
        // Es un alimento personalizado - actualizar en custom_foods
        const customFood = customFoods.find(cf => cf.name === editingFood.originalName);
        if (customFood) {
          const res = await fetch(`${API_URL}/api/admin/offline-generator/custom-foods/${customFood.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: editingFood.name,
              category: editingFood.category,
              subcategory: editingFood.subcategory || '',
              calories: editingFood.calories,
              protein: editingFood.protein,
              carbs: editingFood.carbs,
              fat: editingFood.fat,
              fiber: editingFood.fiber || 0,
              unit: '100g',
              portion_g: 100
            })
          });

          if (!res.ok) throw new Error('Error al actualizar');
        }
      } else {
        // Es un alimento base - crear como override (nuevo alimento personalizado con mismo nombre)
        // Primero excluimos el original
        await fetch(
          `${API_URL}/api/admin/offline-generator/remove-base-food?food_name=${encodeURIComponent(editingFood.originalName)}&category=${editingFood.category}`,
          { method: 'DELETE' }
        );
        
        // Luego creamos uno nuevo con los valores editados
        const res = await fetch(`${API_URL}/api/admin/offline-generator/custom-foods`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: editingFood.name,
            category: editingFood.category,
            subcategory: editingFood.subcategory || '',
            calories: editingFood.calories,
            protein: editingFood.protein,
            carbs: editingFood.carbs,
            fat: editingFood.fat,
            fiber: editingFood.fiber || 0,
            unit: '100g',
            portion_g: 100
          })
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.detail || 'Error al guardar');
        }
      }

      toast.success(`Alimento "${editingFood.name}" actualizado`);
      setIsEditDialogOpen(false);
      setEditingFood(null);
      loadFoods();
      loadCustomFoods();
      loadExcludedFoods();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteFood = async (food) => {
    if (!window.confirm(`¿Eliminar "${food.name}"?`)) return;

    try {
      if (food.is_custom) {
        // Eliminar alimento personalizado
        const customFood = customFoods.find(cf => cf.name === food.name);
        if (customFood) {
          const res = await fetch(`${API_URL}/api/admin/offline-generator/custom-foods/${customFood.id}`, {
            method: 'DELETE'
          });
          if (!res.ok) throw new Error('Error al eliminar');
        }
      } else {
        // Excluir alimento base
        const res = await fetch(
          `${API_URL}/api/admin/offline-generator/remove-base-food?food_name=${encodeURIComponent(food.name)}&category=${selectedCategory}`,
          { method: 'DELETE' }
        );
        if (!res.ok) throw new Error('Error al excluir');
      }

      toast.success(`"${food.name}" eliminado`);
      loadFoods();
      loadCustomFoods();
      loadExcludedFoods();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRestoreFood = async (foodName, category) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/offline-generator/restore-base-food?food_name=${encodeURIComponent(foodName)}&category=${category}`,
        { method: 'DELETE' }
      );

      if (!res.ok) throw new Error('Error al restaurar');

      toast.success(`"${foodName}" restaurado`);
      loadFoods();
      loadExcludedFoods();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredFoods = Array.isArray(foods[selectedCategory]) 
    ? foods[selectedCategory].filter(food =>
        food.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Gestión de Alimentos</h2>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700">
              <FaPlus className="mr-2" /> Agregar Alimento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Agregar Nuevo Alimento</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="Nombre del alimento"
                value={newFood.name}
                onChange={(e) => setNewFood({ ...newFood, name: e.target.value })}
              />
              <Select
                value={newFood.category}
                onValueChange={(v) => setNewFood({ ...newFood, category: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Calorías/100g</label>
                  <Input
                    type="number"
                    value={newFood.calories}
                    onChange={(e) => setNewFood({ ...newFood, calories: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Proteína/100g</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newFood.protein}
                    onChange={(e) => setNewFood({ ...newFood, protein: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Carbos/100g</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newFood.carbs}
                    onChange={(e) => setNewFood({ ...newFood, carbs: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Grasa/100g</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newFood.fat}
                    onChange={(e) => setNewFood({ ...newFood, fat: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">* Todos los valores deben ser por 100g (estándar MyFitnessPal)</p>
              <Button onClick={handleAddFood} className="w-full bg-green-600 hover:bg-green-700 mt-3">
                Agregar Alimento
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtros */}
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <Input
              placeholder="Buscar alimento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Lista de Alimentos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>{CATEGORIES.find(c => c.value === selectedCategory)?.label} ({filteredFoods.length})</span>
            <span className="text-xs font-normal text-gray-500">Valores por 100g</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Alimento</th>
                  <th className="text-center p-2">Kcal</th>
                  <th className="text-center p-2">P</th>
                  <th className="text-center p-2">C</th>
                  <th className="text-center p-2">G</th>
                  <th className="text-center p-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredFoods.map((food, idx) => (
                  <tr key={idx} className={`border-b hover:bg-gray-50 ${food.is_custom ? 'bg-green-50' : ''}`}>
                    <td className="p-2">
                      {food.name}
                      {food.is_custom && <span className="ml-2 text-xs text-green-600 font-medium">(editado)</span>}
                    </td>
                    <td className="text-center p-2">{food.calories}</td>
                    <td className="text-center p-2">{food.protein}g</td>
                    <td className="text-center p-2">{food.carbs}g</td>
                    <td className="text-center p-2">{food.fat}g</td>
                    <td className="text-center p-2">
                      <div className="flex justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenEdit(food)}
                          title="Editar"
                          className="h-8 w-8 p-0"
                        >
                          <FaEdit className="text-blue-500" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteFood(food)}
                          title="Eliminar"
                          className="h-8 w-8 p-0"
                        >
                          <FaTrash className="text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Alimentos Excluidos */}
      {excludedFoods.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-lg text-orange-700">
              Alimentos Eliminados ({excludedFoods.length}) - Click para restaurar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {excludedFoods.map((food, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-orange-300 cursor-pointer hover:bg-orange-100"
                  onClick={() => handleRestoreFood(food.food_name, food.category)}
                >
                  <span className="text-sm">{food.food_name}</span>
                  <FaUndo className="text-green-600 text-xs" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog para editar */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Alimento</DialogTitle>
          </DialogHeader>
          {editingFood && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Nombre</label>
                <Input
                  placeholder="Nombre del alimento"
                  value={editingFood.name}
                  onChange={(e) => setEditingFood({ ...editingFood, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Calorías/100g</label>
                  <Input
                    type="number"
                    value={editingFood.calories}
                    onChange={(e) => setEditingFood({ ...editingFood, calories: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Proteína/100g</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingFood.protein}
                    onChange={(e) => setEditingFood({ ...editingFood, protein: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Carbos/100g</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingFood.carbs}
                    onChange={(e) => setEditingFood({ ...editingFood, carbs: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Grasa/100g</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingFood.fat}
                    onChange={(e) => setEditingFood({ ...editingFood, fat: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              {!editingFood.isCustom && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ Al editar un alimento base, se creará una versión personalizada y el original será excluido.
                </p>
              )}
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveEdit} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Guardar Cambios
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
